import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { trPositionShort } from "@/lib/pesTranslations";

export default function GlobalSearch({ autoFocus = false, onNavigate }) {
    const [q, setQ] = useState("");
    const [res, setRes] = useState(null);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const boxRef = useRef(null);
    const nav = useNavigate();

    /* eslint-disable */
    useEffect(() => {
        const term = q.trim();
        if (term.length < 2) { setRes(null); setOpen(false); setLoading(false); return; }
        setLoading(true);
        setOpen(true);
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/search", { params: { q: term } });
                setRes(data);
            } catch {
                setRes(null);
            } finally {
                setLoading(false);
            }
        }, 250);
        return () => clearTimeout(t);
    }, [q]);

    useEffect(() => {
        const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);
    /* eslint-enable */

    const go = useCallback((to) => {
        setOpen(false);
        setQ("");
        setRes(null);
        onNavigate?.();
        nav(to);
    }, [nav, onNavigate]);

    const players = res?.players || [];
    const teams = res?.teams || [];
    const albo = res?.albo || [];
    const hasResults = players.length || teams.length || albo.length;

    return (
        <div className="relative w-full" ref={boxRef} data-testid="global-search">
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-t5c6370 pointer-events-none text-sm">🔍</span>
            <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onFocus={() => { if (res) setOpen(true); }}
                onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
                placeholder="Cerca giocatori, squadre, mister…"
                autoFocus={autoFocus}
                className="op-input w-full text-sm pr-9"
                data-testid="global-search-input"
            />
            {open && (
                <div
                    className="absolute left-0 right-0 mt-1 bg-t0a0c0f border border-t22252a shadow-2xl z-[60] max-h-[70vh] overflow-y-auto"
                    data-testid="global-search-results"
                >
                    {loading && (
                        <div className="px-3 py-2 op-mono text-xs text-t8f95a3">Ricerca…</div>
                    )}
                    {!loading && !hasResults && (
                        <div className="px-3 py-3 op-mono text-xs text-t8f95a3">Nessun risultato per “{q.trim()}”</div>
                    )}

                    {players.length > 0 && (
                        <div>
                            <div className="px-3 py-1 op-label text-[9px] text-t5c6370 bg-t0e1014 sticky top-0">GIOCATORI</div>
                            {players.map((p) => (
                                <button
                                    key={`p-${p.pes_id ?? p.entry_id}`}
                                    onClick={() => go(`/giocatori/${p.pes_id ?? p.entry_id}`)}
                                    className="w-full text-left px-3 py-2 hover:bg-t16181c transition-colors flex items-center justify-between gap-3"
                                    data-testid={`search-player-${p.pes_id ?? p.entry_id}`}
                                >
                                    <span className="text-sm text-tffffff truncate">
                                        {p.name}
                                        {p.no_pesdb && <span className="op-mono text-[9px] text-tff7a1a ml-1.5 align-middle">no PESDB</span>}
                                    </span>
                                    <span className="op-mono text-[10px] text-t8f95a3 whitespace-nowrap">
                                        {[trPositionShort(p.position), p.overall ? `VG ${p.overall}` : null, p.club].filter(Boolean).join(" · ")}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}

                    {teams.length > 0 && (
                        <div>
                            <div className="px-3 py-1 op-label text-[9px] text-t5c6370 bg-t0e1014 sticky top-0">SQUADRE / MISTER</div>
                            {teams.map((t) => (
                                <button
                                    key={`t-${t.id}`}
                                    onClick={() => go(`/squadre/${t.id}`)}
                                    className="w-full text-left px-3 py-2 hover:bg-t16181c transition-colors flex items-center justify-between gap-3"
                                    data-testid={`search-team-${t.id}`}
                                >
                                    <span className="text-sm text-tffffff truncate">
                                        {t.name}
                                        {t.matched_mister && t.manager_name && (
                                            <span className="text-t2bb6ff"> · {t.manager_name}</span>
                                        )}
                                    </span>
                                    <span className="op-mono text-[10px] text-t8f95a3 whitespace-nowrap">
                                        {[t.manager_name ? `Mister: ${t.manager_name}` : null, t.division].filter(Boolean).join(" · ")}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}

                    {albo.length > 0 && (
                        <div>
                            <div className="px-3 py-1 op-label text-[9px] text-t5c6370 bg-t0e1014 sticky top-0">ALBO D&apos;ORO</div>
                            {albo.map((a) => (
                                <button
                                    key={`a-${a.winner}`}
                                    onClick={() => go("/albo")}
                                    className="w-full text-left px-3 py-2 hover:bg-t16181c transition-colors flex items-center justify-between gap-3"
                                    data-testid={`search-albo-${a.winner}`}
                                >
                                    <span className="text-sm text-tffffff truncate">🏆 {a.winner_display || a.winner}</span>
                                    <span className="op-mono text-[10px] text-t8f95a3 whitespace-nowrap">
                                        {a.trophies} {a.trophies === 1 ? "trofeo" : "trofei"}
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
